import type { HttpTransport } from "@common/api/transport";
import type { CropDefinition } from "@common/types/GardenCrop";
import type { GrowingType } from "@common/types/GrowingType";
import type { TaskType } from "@common/types/TaskTypes";

export type RecommendedTask = {
  type: TaskType;
  startType: "manual" | "relative";
  startDate?: string;
  startRelativeTo?: TaskType;
  startOffset?: number;
  daysDuration?: number;
  isAnchor?: boolean;
  isRepeating?: boolean;
  repeatPeriod?: number;
  repeatPeriodUnit?: "days";
  repeatUntilType?: string;
  repeatUntilRelativeTo?: TaskType;
  resolved: { startDate: string; endDate: string };
};

export type PlantingWindowMarkerGate =
  | "soil_warm"
  | "soil_cools"
  | "soil_hot"
  | "soil_cold"
  | "last_frost"
  | "bolt_onset"
  | "bolt_release"
  | "heat_breaks"
  | "heat_onset"
  | "warmth_arrives"
  | "warmth_ends"
  | "first_frost";

export type PlantingWindowMarker = {
  gate: PlantingWindowMarkerGate;
  date: string;
};

export type PlantingWindowSowDate = {
  date: string;
  bestMatch: boolean;
  harvestStart: string;
  harvestEnd: string;
  tasks: RecommendedTask[];
};

export type PlantingWindow = {
  season: "spring" | "fall" | "winter" | "year_round";
  sow: { earliest: string; recommended: string; latest: string };
  harvest: { start: string; end: string };
  openedBy: string | null;
  closedBy: string | null;
  dates: PlantingWindowSowDate[];
};

export type PlantingWindowsResponse =
  | {
      status: "ok";
      method: GrowingType;
      markers: PlantingWindowMarker[];
      windows: PlantingWindow[];
    }
  | { status: "none"; reason: string };

export type ScheduleRecommendationParams = {
  gardenId: number;
  growingType: GrowingType;
  cropId?: number | string;
  cropVarietyId?: number | string;
  gardenCrop: {
    protectionLevel?: string;
    cropDefinition: Partial<CropDefinition>;
  };
};

export const createScheduleRecommendationsApi = (http: HttpTransport) => ({
  fetchPlantingWindows: ({
    cropId,
    cropVarietyId,
    gardenCrop,
    gardenId,
    growingType
  }: ScheduleRecommendationParams) =>
    http.post<PlantingWindowsResponse>(
      `/api/gardens/${gardenId}/schedule_recommendations`,
      { cropId, cropVarietyId, gardenCrop, growingType }
    )
});
